<template>
  <div  class="modal is-active"
        data-unit="modal is-active"
        @dblclick.stop>
    <div class="modal-background"/>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <strong>{{ $t('add') }}</strong>
        </p>
        <button class="delete"
                @click="cancel"/>
      </header>
      <section class="modal-card-body">
        <loading-icon v-if="isFetchingSuggestions"
                      style="height: 15vh; width: 100%;">
          <template #second-cog>
            <i  class="fa fa-cog fa-stack-2x fa-counter-spin fa-stack-right" 
                style="color: var(--primary--color-invert-text);"/>
          </template>
        </loading-icon>
        <template v-else>
          <div  class="title is-size-5"
                data-int="info-name">
            <label>{{ bomItem.name }}</label>
          </div>
          <div  class="subtitle is-6"
                data-int="info-identifier">
            <label>{{ bomItem.partNumber }}</label>
          </div>
          <div  class="is-flex pb-5 mb-3"
                style="border-bottom: 1px solid lightgrey;">
            <label class="label mr-6">{{ $t('qty') }}</label>
            <div class="is-flex-grow-1">
              <add-to-cart  v-model:quantity="quantity"
                            :expanded="true"
                            :include-add-btn="false"/>
            </div>
          </div>
          <bom-cart-suggestions v-if="showSuggestions"/>
        </template>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button class="button"
                data-unit="cancel-btn"
                @click="cancel">
          {{ $t('cancel') }}
        </button>
        <button :disabled="!canAddToCart"
                class="button is-primary"
                data-unit="add-btn"
                @click="addToCart">
          {{ $t('add') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AddToCart from '../../../../info/AddToCart.vue'
import BomCartSuggestions from './BomCartSuggestions.vue'
import LoadingIcon from "@/components/common/LoadingIcon.vue"
import { useWidgetAddToCart } from '@/components/widgets/composables/useWidgetAddToCart'

export default {
  name: 'BomAddToCartModal',
  props: {
    bomItem: {
      type: Object,
      required: true
    },
    isWidgetCartEnabled: Boolean
  },
  components: { AddToCart, BomCartSuggestions, LoadingIcon },
  data() {
    return {
      quantity: 1,
      isSubmitting: false
    }
  },
  setup() {
    const { emitWidgetAddToCartEventForSuggestions } = useWidgetAddToCart()

    return {
      emitWidgetAddToCartEventForSuggestions
    }
  },
  computed: {
    ...mapState({
      addToCartQtyBehavior: state => state.user.preferences.addToCartQtyBehavior,
      isFetchingSuggestions: state => state.content.bom.cartSuggestions.isLoading
    }),
    ...mapGetters({
      hasPartOrderSuggestions: 'user/hasPartOrderSuggestions',
      hasCartSuggestionsToAdd: 'content/hasCartSuggestionsToAdd',
      hasCartSuggestions: 'content/hasCartSuggestions'
    }),
    showSuggestions() {
      return this.hasPartOrderSuggestions 
        && this.hasCartSuggestions
    },
    canAddToCart() {
      const hasPartQty = this.quantity > 0
      if (this.isSubmitting) {
        return false
      }
      if (this.showSuggestions) {
        return hasPartQty || this.hasCartSuggestionsToAdd
      }
      return hasPartQty
    }
  },
  methods: {
    async addToCart() {
      this.isSubmitting = true
      try {
        if (this.quantity > 0) {
          this.$emit("addToCart", this.quantity)
        } else {
          this.$emit("close")
        }
        if (this.hasCartSuggestionsToAdd) {
          if (this.isWidgetCartEnabled) {
            await this.emitWidgetAddToCartEventForSuggestions()
          } else {
            await this.$store.dispatch("content/addSuggestionsToCart")
          }
        }
      } catch { 
        console.error("error adding to cart")
        this.$emit("close")
      } finally {
        this.isSubmitting = false
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    getInitialQuantity() {
      switch (this.addToCartQtyBehavior) {
        case 'qtyZero':
          return 0
        case 'qtyFromBom':
          return this.bomItem.quantity ? parseInt(this.bomItem.quantity, 10) : 1
        default:
          return 1
      }
    },
    escEventListener({ key }) {
      if (key === 'Escape' || key === 'Esc') {
        this.$emit('close')
      }
    }
  },
  async mounted() {
    window.addEventListener('keyup', this.escEventListener)
    await this.$store.dispatch('content/updateTocOpen')
    this.quantity = this.getInitialQuantity()
    await this.$store.dispatch(
      "content/getCartSuggestions", 
      this.bomItem.id
    )
  },
  unmounted() {
    window.removeEventListener('keyup', this.escEventListener)
    this.$store.dispatch("content/clearCartSuggestions")
  }
}
</script>

<style scoped>
.modal-card {
  max-width: 60%;
  min-width: 10rem;
}
@media only screen and (max-width: 768px) {
  .modal-card {
    max-width: 90%;
  }
}

.modal-card-body {
  flex-direction: column;
  min-height: 12rem;
}

.input {
  min-width: 10rem;
}
</style>
